import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { enGB } from 'date-fns/locale';
import {
  CalendarBlank,
  Clock,
  MapPin,
  VideoCamera,
  Buildings,
  HourglassMedium,
  CheckCircle
} from '@phosphor-icons/react';
import { useTheme, alpha } from '@mui/material/styles';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import { cardStyles } from '../../theme/componentStyles';
import PoweredByIdotoo from '../Common/PoweredByIdotoo';

const pageBackgroundSx = {
  minHeight: '100vh',
  py: { xs: 1.5, sm: 2 },
  px: { xs: 1, sm: 2 },
  backgroundImage: cardStyles.gradient.backgroundImage,
  backgroundAttachment: 'fixed'
};

function monthRangeFromDate(d) {
  if (!d || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = d.getMonth();
  const start = new Date(y, m, 1, 0, 0, 0, 0);
  const end = new Date(y, m + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

function dateKey(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfDay(d) {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function groupSlotsByDate(rawSlots) {
  const grouped = new Map();
  const now = new Date();
  const todayKey = dateKey(now);

  (rawSlots || []).forEach((slot) => {
    const start = new Date(slot.startTime);
    if (Number.isNaN(start.getTime())) return;

    const key = dateKey(start);
    if (key === todayKey && start <= now) {
      return;
    }

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(slot);
  });

  grouped.forEach((daySlots, key) => {
    daySlots.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    grouped.set(key, daySlots);
  });

  return grouped;
}

function formatSlotLabel(isoStart) {
  const dt = new Date(isoStart);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function locationTypeLabel(locationType) {
  switch (locationType) {
    case 'virtual':
      return 'Virtual';
    case 'off_site':
      return 'Off-site';
    case 'on_site':
    default:
      return 'On-site';
  }
}

const STEPS = ['Choose date', 'Choose time', 'Your details'];

/** Matches DateCalendar layout (7 × 36px cells + picker chrome); right column uses this width on md+. */
const CALENDAR_PANEL_WIDTH_PX = 320;

export default function PublicEmbedBookPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const styles = useThemeStyles();
  const dividerBorder = `1px solid ${theme.palette.divider}`;

  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState('');
  const [config, setConfig] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());

  const [monthLoading, setMonthLoading] = useState(false);
  const [monthSlotsByDate, setMonthSlotsByDate] = useState(() => new Map());
  const [monthLoadError, setMonthLoadError] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  const utmPayload = useMemo(() => {
    return {
      utmSource: searchParams.get('utm_source') || searchParams.get('utmSource') || undefined,
      utmMedium: searchParams.get('utm_medium') || searchParams.get('utmMedium') || undefined,
      utmCampaign: searchParams.get('utm_campaign') || searchParams.get('utmCampaign') || undefined
    };
  }, [searchParams]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setConfigError('');
    try {
      const res = await api.get(`/api/public/booking-embed/${token}`);
      setConfig(res.data);
    } catch (err) {
      setConfigError(err.response?.data?.message || 'This booking page is not available.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadMonthAvailability = useCallback(async (referenceDate) => {
    if (!token || !config?.serviceType?.id) return;
    const range = monthRangeFromDate(referenceDate);
    if (!range) return;

    setMonthLoading(true);
    setMonthLoadError('');
    try {
      const duration = config.serviceType.duration || 30;
      const res = await api.get(`/api/public/booking-embed/${token}/availability`, {
        params: {
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
          slotDuration: duration
        }
      });
      setMonthSlotsByDate(groupSlotsByDate(res.data.availableSlots || []));
    } catch (err) {
      setMonthLoadError(err.response?.data?.message || 'Could not load availability for this month.');
      setMonthSlotsByDate(new Map());
    } finally {
      setMonthLoading(false);
    }
  }, [token, config]);

  useEffect(() => {
    if (config) {
      loadMonthAvailability(visibleMonth);
    }
  }, [config, visibleMonth, loadMonthAvailability]);

  useEffect(() => {
    const key = dateKey(calendarDate);
    setSlots(monthSlotsByDate.get(key) || []);
    setSelectedSlot(null);
  }, [calendarDate, monthSlotsByDate]);

  const isDateSelectable = useCallback((date) => {
    if (!date || Number.isNaN(date.getTime())) return false;
    if (startOfDay(date) < startOfDay(new Date())) return false;
    return (monthSlotsByDate.get(dateKey(date)) || []).length > 0;
  }, [monthSlotsByDate]);

  const shouldDisableCalendarDate = useCallback((date) => {
    if (monthLoading) return true;
    return !isDateSelectable(date);
  }, [monthLoading, isDateSelectable]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (!selectedSlot) {
      setSubmitError('Please choose a time first.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.post(`/api/public/booking-embed/${token}`, {
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim(),
        clientNotes: clientNotes.trim(),
        location: {
          type: config?.serviceType?.locationType || 'on_site',
          ...(config?.serviceType?.locationAddress
            ? { address: config.serviceType.locationAddress }
            : {})
        },
        website: honeypot,
        idotoo_hp: honeypot,
        ...utmPayload
      });
      setDone(true);
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const calendarValue = isDateSelectable(calendarDate) ? calendarDate : null;

  const goNext = () => {
    setSubmitError('');
    if (activeStep === 0) {
      if (monthLoading) {
        setSubmitError('Still loading available dates. Please wait a moment.');
        return;
      }
      if (!isDateSelectable(calendarDate)) {
        setSubmitError('Please pick a date with available times.');
        return;
      }
    }
    if (activeStep === 1 && !selectedSlot) {
      setSubmitError('Please choose a time to continue.');
      return;
    }
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setSubmitError('');
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  if (loading) {
    return (
      <Box sx={{ ...pageBackgroundSx, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <PoweredByIdotoo />
      </Box>
    );
  }

  if (configError || !config) {
    return (
      <Box sx={pageBackgroundSx}>
        <Box sx={{ maxWidth: 560, mx: 'auto', pt: 4 }}>
          <Alert severity="error">{configError || 'This booking page is not available.'}</Alert>
          <PoweredByIdotoo />
        </Box>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={pageBackgroundSx}>
        <Box sx={{ maxWidth: 560, mx: 'auto', pt: 4 }}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 2, ...(styles.text?.pagename || {}) }}>
              Request sent
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Thanks, {clientName.trim()}. Your appointment request is with {config.business?.name}. They will confirm by email.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you do not hear back within a couple of days, check your spam folder or contact them directly.
            </Typography>
          </Paper>
          <PoweredByIdotoo />
        </Box>
      </Box>
    );
  }

  const st = config.serviceType;
  const biz = config.business;

  const iconMetaTextSx = {
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    pt: 0.125,
    color: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.72 : 0.82)
  };

  const locationIcon = (() => {
    const lt = st?.locationType;
    if (lt === 'virtual') return <VideoCamera size={24} weight="duotone" aria-hidden />;
    if (lt === 'off_site') return <MapPin size={24} weight="duotone" aria-hidden />;
    return <Buildings size={24} weight="duotone" aria-hidden />;
  })();

  const summaryBlock = (
    <Box
      sx={{
        flex: { xs: 'none', md: '0 0 auto' },
        alignSelf: { xs: 'stretch', md: 'stretch' },
        minWidth: { md: 228 },
        maxWidth: { xs: '100%', md: 300 },
        display: 'flex',
        flexDirection: 'column',
        minHeight: { md: 'min(100%, 440px)' },
        px: { xs: 2.25, sm: 2.5, md: 2.75 },
        pb: { xs: 2.25, sm: 2.5, md: 2.75 },
        pt: { xs: 3, sm: 3.25, md: 3.5 },
        bgcolor: 'background.paper',
        borderRight: { md: dividerBorder },
        borderBottom: { xs: dividerBorder, md: 'none' }
      }}
    >
      <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
        {biz?.logoUrl ? (
          <Box
            component="img"
            src={biz.logoUrl}
            alt={biz?.name ? `${biz.name} logo` : 'Business logo'}
            sx={{
              maxHeight: 64,
              maxWidth: '100%',
              width: 'auto',
              objectFit: 'contain',
              mb: 2,
              display: 'block'
            }}
          />
        ) : null}
        <Typography variant="overline" color="text.secondary" display="block" sx={{ lineHeight: 1.2, letterSpacing: 1, mb: 0.75 }}>
          {biz?.name}
        </Typography>
        <Typography
          variant="h6"
          component="h1"
          sx={{
            mt: 1.25,
            mb: 1,
            ...(styles.text?.pagename || {}),
            fontSize: { xs: '1.15rem', sm: '1.3rem' }
          }}
        >
          {st?.name}
        </Typography>
        {st?.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '0.875rem', lineHeight: 1.5 }}>
            {st.description}
          </Typography>
        ) : null}

        <Stack spacing={2.25}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box sx={{ flexShrink: 0, lineHeight: 0, pt: 0.25, color: 'text.secondary' }}>
              <Clock size={24} weight="duotone" aria-hidden />
            </Box>
            <Typography variant="body2" sx={iconMetaTextSx}>
              {st?.duration || 0} minutes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box sx={{ flexShrink: 0, lineHeight: 0, pt: 0.25, color: 'text.secondary' }}>{locationIcon}</Box>
            <Typography variant="body2" sx={iconMetaTextSx}>
              {locationTypeLabel(st?.locationType)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box sx={{ flexShrink: 0, lineHeight: 0, pt: 0.25, color: 'text.secondary' }}>
              {st?.requiresApproval ? (
                <HourglassMedium size={24} weight="duotone" aria-hidden />
              ) : (
                <CheckCircle size={24} weight="duotone" aria-hidden />
              )}
            </Box>
            <Typography variant="body2" sx={iconMetaTextSx}>
              {st?.requiresApproval
                ? 'The supplier will confirm this booking via email.'
                : 'Confirmed as soon as you send this.'}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Typography
        variant="caption"
        component="p"
        sx={{
          mt: 'auto',
          pt: 3,
          mb: 0,
          color: 'text.secondary',
          fontSize: '0.7rem',
          fontWeight: 500,
          letterSpacing: 0.02
        }}
      >
        Step {activeStep + 1} of {STEPS.length}
      </Typography>
    </Box>
  );

  const mainBlock = (
    <Box
      sx={{
        flex: { xs: 'none', md: '0 0 auto' },
        width: { xs: '100%', md: CALENDAR_PANEL_WIDTH_PX },
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        p: { xs: 2, sm: 2.25 },
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
        {activeStep === 0 ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <CalendarBlank size={18} aria-hidden />
              Pick a date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DateCalendar
                value={calendarValue}
                referenceDate={visibleMonth}
                loading={monthLoading}
                disablePast
                shouldDisableDate={shouldDisableCalendarDate}
                onMonthChange={(month) => {
                  if (month) {
                    setVisibleMonth(month);
                  }
                }}
                onChange={(d) => {
                  if (d && isDateSelectable(d)) {
                    setCalendarDate(d);
                    setSubmitError('');
                  }
                }}
                sx={{
                  width: '100%',
                  maxWidth: CALENDAR_PANEL_WIDTH_PX,
                  mx: { xs: 'auto', md: 0 },
                  '& .MuiPickersCalendarHeader-root': { px: 0.5, py: 0.5, minHeight: 40 },
                  '& .MuiDayCalendar-weekContainer': { mx: 0 },
                  '& .MuiPickersArrowSwitcher-root': { gap: 0.5 },
                  '& .MuiPickersCalendarHeader-labelContainer': { fontSize: '0.95rem' },
                  '& .MuiDayCalendar-header': { justifyContent: 'space-around' },
                  '& .MuiPickersDay-root': { width: 36, height: 36, fontSize: '0.8rem' },
                  '& .MuiPickersDay-root.Mui-disabled:not(.Mui-selected)': {
                    color: theme.palette.action.disabled,
                    opacity: 0.45
                  }
                }}
              />
            </LocalizationProvider>
            {monthLoadError ? (
              <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>{monthLoadError}</Alert>
            ) : null}
            {submitError && activeStep === 0 ? (
              <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>{submitError}</Alert>
            ) : null}
          </Box>
        ) : null}

        {activeStep === 1 ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Times on{' '}
              {calendarDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
            </Typography>
            {monthLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : monthLoadError ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>{monthLoadError}</Alert>
            ) : slots.length === 0 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>No times left on this day. Go back and pick another date.</Alert>
            ) : (
              <Grid container spacing={0.75}>
                {slots.map((slot, idx) => {
                  const key = `${slot.startTime}-${idx}`;
                  const selected =
                    selectedSlot &&
                    selectedSlot.startTime === slot.startTime &&
                    selectedSlot.endTime === slot.endTime;
                  return (
                    <Grid item xs={4} key={key}>
                      <Button
                        fullWidth
                        size="small"
                        variant={selected ? 'contained' : 'outlined'}
                        onClick={() => setSelectedSlot(slot)}
                        sx={{
                          py: 1.1,
                          px: 0.5,
                          minWidth: 0,
                          minHeight: 40,
                          fontSize: '0.8125rem',
                          lineHeight: 1.25,
                          borderRadius: '12px',
                          textTransform: 'none'
                        }}
                      >
                        {formatSlotLabel(slot.startTime)}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        ) : null}

        {activeStep === 2 ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Your details
            </Typography>
            <input
              type="text"
              name="idotoo_hp"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  label="Full name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone (optional)"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes (optional)"
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
            {submitError ? (
              <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                {submitError}
              </Alert>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center" sx={{ mt: 2, pt: 1.5, borderTop: dividerBorder }}>
        <Button size="small" onClick={goBack} disabled={activeStep === 0 || submitting}>
          Back
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button size="small" variant="contained" onClick={goNext}>
            Next
          </Button>
        ) : (
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={() => handleSubmit()}
            disabled={submitting || !selectedSlot}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Send request'}
          </Button>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box sx={pageBackgroundSx}>
      <Box sx={{ width: 'fit-content', maxWidth: '100%', mx: 'auto' }}>
        <Paper
          elevation={2}
          sx={{
            width: 'fit-content',
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch'
          }}
        >
          {summaryBlock}
          {mainBlock}
        </Paper>
        <PoweredByIdotoo />
      </Box>
    </Box>
  );
}
